// @flow

import moment from 'moment';
import { includes, inRange, isNumber, pick, isEmpty, uniqWith, isEqual } from 'lodash';
import { type Kids } from 'Store/trip-information/kids/kids.reducer';
import {
  ADULTS_ONLY_FACET_ID,
  CHILDCARE_FACET_ID,
  DEFAULT_CHILDCARE_FACETS,
  DEPARTURE_DATE_DEFAULT_DELAY_IN_MONTH,
} from './constants';

type ChildcareFacetInput = {
  label: string,
  value: string,
};

type ChildcareFacetOutput = { id: string, name: ?string | boolean };

export class FacetFilters {
  _list: ChildcareFacetOutput[];

  constructor(list: ChildcareFacetOutput[]) {
    this._list = list || [];
  }

  addFilter(filter: ChildcareFacetOutput) {
    this._list.push({
      id: filter.id,
      name: filter.name,
    });

    return this;
  }

  addFilters(filters: ChildcareFacetOutput[]) {
    if (filters) {
      filters.map(this.addFilter.bind(this));
    }

    return this;
  }

  removeFilters(ids: string[]) {
    this._list = this._list.filter(item => !includes(ids, item.id));

    return this;
  }

  addChildcareFilters(args: {
    kids: Kids[],
    childcareFacets?: ChildcareFacetInput[],
    departureDate?: string,
  }): this {
    const { kids, childcareFacets: facets = [], departureDate } = args;
    const childcareFacets = isEmpty(facets)
      ? DEFAULT_CHILDCARE_FACETS.map(f => ({ label: f, value: f }))
      : facets;
    const childcareRanges = childcareFacets.map(({ label: name }) => {
      const facetRange = name.split('-');
      return {
        name,
        min: parseInt(facetRange[0], 10),
        max: parseInt(facetRange[1], 10),
      };
    });

    const childcareFilters = kids
      .filter(({ birthdate }) => isNumber(birthdate))
      .filter(({ childcareIncluded }) => childcareIncluded)
      .map(({ birthdate }) => {
        // if no departureDate is given, the kid's age is calculated for a departureDate in 6 months
        const defaultDepartureDate = () =>
          moment()
            .add(DEPARTURE_DATE_DEFAULT_DELAY_IN_MONTH, 'months')
            .valueOf();

        const kidAgeInMonths = moment(departureDate || defaultDepartureDate()).diff(
          moment(birthdate),
          'months',
        );

        const matchingFacet = childcareRanges.find(({ min, max }) =>
          inRange(kidAgeInMonths, min, max + 1),
        ); // lodash inRange exludes the max value

        if (!matchingFacet || !matchingFacet.name) {
          return null;
        }

        return {
          id: CHILDCARE_FACET_ID,
          name: matchingFacet.name,
        };
      })
      .filter(Boolean);

    this._list = this._list.concat(uniqWith(childcareFilters, isEqual));
    return this;
  }

  addAdultOnlyFilter(kids: Kids[] = []) {
    return isEmpty(kids) ? this : this.addFilter({ id: ADULTS_ONLY_FACET_ID, name: false });
  }

  value() {
    return this._list.map<ChildcareFacetOutput>(item => pick(item, ['id', 'name']));
  }
}
